import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import LogoWrapper from "../LogoWrapper/LogoWrapper";
import ExpiredNotice from "./ExpiredNotice.js";
import FlausFlosserConfirmLink from "./FlausFlosserConfirmLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useFont } from "../../contexts/FontContext/FontContext";
import { fetchReturnData } from "../utils/common";
import { useLogo } from "../../contexts/LogoContext/LogoContext";
import useScreenWidth from "../../hooks/useScreenWidth";
import useExpiration from "../../hooks/useExpiration";
import usePortalSession from "../../hooks/usePortalSession";
import { trackingColors } from "../utils/common";
import styles from "./ReturnLink.module.css";

const ReturnLink = ( { isConfirmation, isExchange } ) => {
	const apiUrl = process.env.REACT_APP_API_URL;
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editableFields, setEditableFields] = useState({});
    const [error, setError] = useState(null);
    const [totalCost, setTotalCost] = useState(0);
    const isAuthenticated = !!localStorage.getItem("authToken");
    const [fadeInReturnItems, setFadeInReturnItems] = useState("no_opacity");
    const [fadeInReturnInfo, setFadeInReturnInfo] = useState("no_opacity");
    const [exchangesBoxScrollable, setExchangesBoxScrollable] = useState(false);
    const [returnsBoxScrollable, setReturnsBoxScrollable] = useState(false);
    const [showTitle, setShowTitle] = useState(false);
    const [showContainer, setShowContainer] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const [showReturnDetailsLoader, setShowReturnDetailsLoader] = useState(true);
    const [showNewItems, setShowNewItems] = useState(false);
   	const [trackingStatus, setTrackingStatus] = useState("unknown");
   	const [trackingCode, setTrackingCode] = useState("not available");
   	const [itemQuantity, setItemQuantity] = useState(null);
    const fontSchemes = useFont();    
    const fontFamilyOne = fontSchemes.scheme_one; 
    const location = useLocation();
    const screenWidth = useScreenWidth();
    const queryParams = new URLSearchParams(location.search);
    const navigate = useNavigate();
    const logoUrl = useLogo();
    var getExchangeData = false;
    var token = queryParams.get("token");
    if (token.includes("confirm")) { token = token?.split('/')[0]; }
    const expirationStatus = useExpiration(apiUrl, token);
    const sessionId = usePortalSession(token, apiUrl);

    useEffect(() => {
	    if (token) {
	        const getExchangeData = isExchange ? true : false;
	        fetchReturnData(token, apiUrl, setOrderData, setTotalCost, setError, setEditableFields, getExchangeData);
	        setLoading(false);
	    }
	}, [token, isExchange]);

    // Redirect if trying to access pre-confirmation page after label purchase
    useEffect(() => {
        const checkReturnStatus = async () => {
            try {
                const response = await fetch(`${apiUrl}/api/check_return_status/${token}/`);
                const data = await response.json();        
                if (!isConfirmation && data.status) { navigate(`/returns_confirm/${data.order_id}?token=${token}`); // If trying to access pre-confirmation page after purchase, redirect to post-confirmation                   
                } else if (isConfirmation && !data.status) { navigate(`/${isExchange ? "exchanges" : "returns"}/${data.order_id}?token=${token}`); } // Redirect to the start page if attempting to access post-confirmation before purchase
            } catch (err) { console.log("error checking return status: ", err); }               
            finally { setLoading(false); }
        };
        checkReturnStatus();
    }, [token]);

    //tally up all items within each product to get the total number of items
    useEffect(() => {
    	if (orderData) {
    		let num_items = 0;
    		orderData.products.forEach(product => { num_items += product.product_quantity; }); 
    		setItemQuantity(num_items);
    		setReturnsBoxScrollable(orderData.products.length > 1);
    		setExchangesBoxScrollable(orderData.new_items.length > 1);
    	}
    }, [orderData]);

    // Trigger fade-ins with staggered timeouts
    useEffect(() => {
        if (!loading && !error) {
            setTimeout(() => setShowContainer(true), 250); 
            setTimeout(() => setShowTitle(true), 750); 
            setTimeout(() => setShowReturnDetailsLoader(false), 1800); 
            setTimeout(() => setFadeInReturnItems("fade_in"), 2000); //can be done same way as others, just playing around with syntax here
            setTimeout(() => setFadeInReturnInfo("fade_in"), 2200);
            setTimeout(() => setShowButton(true), 2500); 
        }
    }, [loading, error]);    

    useEffect(() => { saveChanges(); }, [editableFields]); // make API call to save changes to backend

    //grab tracking code/status if confirmation
    useEffect(() => {
    	const fetchTrackingData = async () => {
	    	try {
	        	const response = await fetch(`${apiUrl}/api/get_tracking_status/${token}/`)
	        	const data = await response.json();
	        	setTrackingCode(data.tracking_code);
	        	setTrackingStatus(data.tracking_status);
	    	} catch (err) { setError(err.message);  }
    	}
    	if (isConfirmation && token) { fetchTrackingData(); }
    }, [token]);

    // Compute the new editableFields object
    const handleInputChange = (field, value) => {
    	let cleanedValue = value.replace(/^0+/, "") || "0"; // Remove leading zeros (unless input is just "0")
	    const updatedEditableFields = {...editableFields, [field]: cleanedValue, 
	};
	    // Update editableFields and orderData using the updated values
	    setEditableFields(updatedEditableFields);
	    setOrderData((prev) => ({...prev, ...updatedEditableFields, }));
	};

	const saveChanges = async () => {
        try {
            const response = await fetch(`${apiUrl}/api/update_return_order_data/${token}/`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({...orderData, ...editableFields, }),
            });
            const updatedData = await response.json();
        } catch (err) { console.log("Error updating return order data:", err); }
    };

    //disable continue if any numeric field is invalid
    const areNumberInputsValid = () => {
	    const requiredFields = ["length", "width", "height", "weight"];
	    return requiredFields.every((field) => {
	        const value = editableFields[field];
	        return value && value !== "" && value !== "0" && !/^0\d+/.test(value); // Rejects "0", empty, and leading zeros
	    });
	};

	//highlight border of invalid fields
	const getInputClass = (field) => {
	    const value = editableFields[field];
	    return !value || value === "0" || /^0\d+/.test(value) ? styles.invalid_input : "";
	};

    return (
    	<>
	    	{!loading && orderData && !expirationStatus && isConfirmation && orderData?.business_name?.toLowerCase() === "flaus" && (
		    		<FlausFlosserConfirmLink
		    			orderData={orderData}
		    			trackingStatus={trackingStatus}
		    			trackingCode={trackingCode}
		    			logoUrl={logoUrl}
		    			itemQuantity={itemQuantity}
		    		/>
		    	)}
		    	{!loading && orderData && !expirationStatus && !(isConfirmation && orderData?.business_name?.toLowerCase() === "flaus") && (
		        <div className={`${styles.return_link_container} ${isAuthenticated ? styles.subtract_height : styles.add_height} ${fontFamilyOne}`}>
		            <Helmet>
		                <title>Start return</title>
		            </Helmet>
		            {screenWidth > 481 ? (<LogoWrapper src={logoUrl ? `${process.env.PUBLIC_URL}/business_images/${logoUrl}` : `${process.env.PUBLIC_URL}/images/partial_logo.png`} class_={styles.logo_wrapper}/>) : null}
		            {isConfirmation && (
		            	<div className={styles.return_confirm_details_wrapper}>
		            		<p className={`titles ${styles.confirmation_title} ${showTitle ? "fade_in" : "no_opacity"}`}>Details and tracking status for order #{orderData.order_id}</p>
		            		<p className={`titles ${styles.return_label_sent} ${showTitle ? "fade_in" : "no_opacity"}`}>Return label sent to {orderData.customer_email}</p>
		            		<p className={`titles ${styles.save_link_text} ${showTitle ? "fade_in" : "no_opacity"}`}>(Save this link for tracking updates)</p>
		            	</div>
		            )}
		            {!isConfirmation && (<p className={`titles ${styles.start_return_title} ${showTitle ? "fade_in" : "no_opacity"}`}>{isAuthenticated ? "Edit" : "Start"} return for order #{orderData.order_id}</p>)}       
		            <div className={`${styles.return_details_container} ${showContainer ? "fade_in" : "no_opacity"} ${orderData.products.length === 1 ? styles.shrink_container : styles.move_up}`}>
		            	{showReturnDetailsLoader && (<img className={`${styles.returns_link_details_loader} ${orderData.products.length === 1 ? styles.shift_loader_up : ""}`} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
		            	{!showReturnDetailsLoader && !showNewItems && (
		            		<>
				           		<div className={`${styles.return_items_container} ${fadeInReturnItems} ${returnsBoxScrollable ? styles.return_items_container_scrollable : styles.return_items_container_not_scrollable}`}>
					                {orderData.products.map((product, index) => (
					                	<div key={index} className={`${styles.return_order_item} ${index !== orderData.products.length - 1 ? "bottom_box_shadow" : ""}`}>
					                		<div className={styles.product_title_wrappers}>
					                			<p className={`${styles.product_titles}`}>{product.product_title} / {product.product_variant && `${product.product_variant}`} {product.product_quantity > 1 ? `(${product.product_quantity} items)` : ""}</p>
					                		</div>
					                		<img className={styles.product_images} src={product.product_image} alt={product.product_title} />
					                	</div>
					                ))}
				           		</div>
				           		<div className={`${styles.return_info_container} ${fadeInReturnInfo}`}>
				           			<div className={`${styles.item_count_wrapper} ${isConfirmation ? styles.confirm : ""}`}>
				           				<p className={`${styles.item_count}`}>Returning {itemQuantity} {itemQuantity === 1 ? "item" : "items"}</p>
				           			</div>
				           			<div className={styles.return_info_wrapper}>								
				           				{/* Render tracking status if in confirmation mode */}
									    {isConfirmation && (
									    	<>									  
											    <div className={`${styles.return_info_text_wrappers} ${isConfirmation ? styles.confirm : ""}`}>
										            <p className={`${styles.return_info} ${styles.return_info_label}`}>Tracking Status:</p>
										            <p className={`${styles.return_info} ${styles.return_info_value}`} style={{color: trackingColors[trackingStatus]}}>{trackingStatus.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase())}</p> {/*Capitalize first letter and remove underscores*/}
										        </div>
										        <div className={`${styles.return_info_text_wrappers} ${isConfirmation ? styles.confirm : ""}`}>
											        <p className={`${styles.return_info} ${styles.return_info_label}`}>Tracking Code:</p>
											        <p className={`${styles.return_info} ${styles.return_info_value}`}>{trackingCode}</p>
											    </div>
											</>
									    )}
				           				{Object.keys(editableFields)
				           					.filter((field) => !["length", "width", "height", "weight", "business_logo", "business_name", "ship_to_address"].includes(field) && !(isConfirmation && field === "customer_email"))
				           					.map((field) => (
				           						<div key={field} className={`${styles.return_info_text_wrappers} ${isConfirmation ? styles.confirm : ""}`}>
				           							 <p className={`${styles.return_info} ${styles.return_info_label}`}> {field.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase())}</p>
				           							 {isAuthenticated && !isExchange ? (<input className={`${styles.return_info} ${styles.return_info_value}`} value={editableFields[field]} onChange={(e) => handleInputChange(field, e.target.value)} />)
				           							 : (<p className={`${styles.return_info} ${styles.return_info_value} ${field === "customer_address" ? styles.address_font : ""}`}>{editableFields[field]}{" "}</p>)}		
				           						</div>
				           					))
				           				}
				           				<div className={`${styles.return_info_text_wrappers} ${isConfirmation ? styles.confirm : ""}`}>
				           					<p className={`${styles.return_info} ${styles.return_info_label}`}>Package weight</p>
				           					<input type="number" className={`${styles.return_info} ${styles.return_info_value} ${styles.numeric_input} ${getInputClass("weight")}`} value={editableFields["weight"]} onChange={(e) => handleInputChange("weight", e.target.value)} readOnly={isConfirmation} />
				           					<span className={`${styles.unit_label} ${styles.oz_label}`}>oz.</span>
				           				</div>
				           				<div className={styles.package_dimensions_wrapper}>
										    <p className={`${styles.package_info} ${styles.package_info_label}`}>Dimensions &nbsp; <span style={{ fontWeight: "bold" }}>L</span></p>
										    <input type="number" className={`${styles.package_info} ${styles.numeric_input} ${styles.package_info_value} ${getInputClass("length")}`} value={editableFields["length"]} onChange={(e) => !isConfirmation && /^\d*\.?\d*$/.test(e.target.value) && handleInputChange("length", e.target.value)} readOnly={isConfirmation} />
										    <span className={styles.unit_label}>in.</span>
										    <p className={`${styles.package_info} ${styles.package_info_label}`}><span style={{ fontWeight: "bold" }}>W</span></p>
										    <input type="number" className={`${styles.package_info} ${styles.numeric_input} ${styles.package_info_value} ${getInputClass("width")}`} value={editableFields["width"]} onChange={(e) => !isConfirmation && /^\d*\.?\d*$/.test(e.target.value) && handleInputChange("width", e.target.value)} readOnly={isConfirmation} />
										    <span className={styles.unit_label}>in.</span>
										    <p className={`${styles.package_info} ${styles.package_info_label}`}><span style={{ fontWeight: "bold" }}>H</span></p>
										    <input type="number" className={`${styles.package_info} ${styles.numeric_input} ${styles.package_info_value} ${getInputClass("height")}`} value={editableFields["height"]} onChange={(e) => !isConfirmation && /^\d*\.?\d*$/.test(e.target.value) && handleInputChange("height", e.target.value)} readOnly={isConfirmation} />
										    <span className={styles.unit_label}>in.</span>
										</div>
									</div>
								</div>
				           	</>
			           	)}
						{!showReturnDetailsLoader && showNewItems && (
							<div className={styles.new_items_box_container}>
								<p className={styles.your_new_items}>Your new items</p>
								<div className={`${styles.new_items_box} ${fadeInReturnItems} ${exchangesBoxScrollable ? styles.return_items_container_scrollable : styles.return_items_container_not_scrollable}`}>
					                {orderData.new_items.map((product, index) => (
					                	<div key={index} className={`${styles.return_order_item} ${index !== orderData.new_items.length - 1 ? "bottom_box_shadow" : ""}`}>
					                		<div className={styles.product_title_wrappers}>
					                			<p className={`${styles.product_titles}`}>{product.product_title}{product.product_variant && ` / ${product.product_variant}`} {product.product_quantity > 1 ? `(${product.product_quantity} items)` : ""}</p>
					                		</div>
					                		<img className={styles.product_images} src={product.product_image} alt={product.product_title} />
					                	</div>
					                ))}
				           		</div>				     
				           		<p className={styles.ship_to_address_label}>Shipping to:</p>
				           		<div className={styles.ship_to_address_container}>
				           			<p className={styles.ship_to_address}>{orderData.ship_to_address}</p>
				           		</div>
							</div>
						)}
		            </div>
		            {!showReturnDetailsLoader && !isAuthenticated && !isConfirmation && (<div className={`continue_button ${styles.continue_button} ${showButton ? "fade_in" : "no_opacity"}`} onClick={() => { if (areNumberInputsValid()) { navigate(`/select_return_option/${orderData.order_id}?token=${token}`, { state: { quantity: itemQuantity }})}}}>Continue</div>)}     
		            {!showReturnDetailsLoader && isExchange && isConfirmation && (<div className={`continue_button fade_in ${styles.see_new_items}`} onClick={() => { setShowNewItems(!showNewItems); }}>{showNewItems ? "See return items" : "See new items"}</div>)}  
		        </div>
		    )}
			{expirationStatus && (<ExpiredNotice font={fontFamilyOne} linkType={"return"} />)}
		</>
    );
};

export default ReturnLink;